type Callback<T = unknown> = (args: T) => void;

interface EventCallbacksPair<T = unknown> {
    eventType: string;
    callbacks: Callback<T>[];
}

/**
 * EventBus
 *
 * This class provides a centralized event handling mechanism for facilitating
 * communication between unrelated Stimulus controllers or modules in the application.
 *
 * By decoupling event handling from the DOM, the EventBus allows for cleaner
 * and more maintainable communication patterns, reducing the reliance on custom
 * DOM events or direct references between controllers.
 *
 * Integration with Stimulus:
 *
 * See the index.ts module in the same directory for helper functions that integrate the EventBus.
 *
 * Import the helpers and use them in your Stimulus controllers to subscribe, unsubscribe, and dispatch events
 * without needing to directly interact with the EventBus instance.
 *
 * As patterns emerge, compose concrete helpers i.e openModal().
 */
export class EventBus {
    protected eventCallbacksPairs: EventCallbacksPair[] = [];

    subscribe<T = unknown>(eventType: string, callback: Callback<T>): void {
        const eventCallbacksPair = this.findEventCallbacksPair(eventType);

        if (eventCallbacksPair) {
            eventCallbacksPair.callbacks.push(callback);
        } else {
            this.eventCallbacksPairs.push({
                eventType,
                callbacks: [callback],
            });
        }
    }

    unsubscribe<T = unknown>(eventType: string, callback: Callback<T>): void {
        const eventCallbacksPair = this.findEventCallbacksPair(eventType);

        if (eventCallbacksPair) {
            eventCallbacksPair.callbacks = eventCallbacksPair.callbacks.filter(cb => cb !== callback);

            if (eventCallbacksPair.callbacks.length === 0) {
                this.eventCallbacksPairs = this.eventCallbacksPairs.filter(pair => pair !== eventCallbacksPair);
            }
        }
    }

    /**
     * Post an event to be handled by a registered subscriber
     * console.error statement left in purposefully for debug/testing
     */
    post<T = unknown>(eventType: string, args: T): void {
        const eventCallbacksPair = this.findEventCallbacksPair(eventType);
        if (eventCallbacksPair) {
            eventCallbacksPair.callbacks.forEach(callback => {
                try {
                    callback(args);
                } catch(_error) {
                    // eslint-disable-next-line no-console
                    console.error(`Error in EventBus callback for event "${eventType}":`, _error);
                }
            });
        }
    }

    getSubscribersCount(eventType?: string): number {
        if (typeof eventType !== 'string' || eventType.trim() === '') {
            return this.eventCallbacksPairs.reduce(
                (total, pair) => total + pair.callbacks.length,
                0
            );
        }

        return this.findEventCallbacksPair(eventType)?.callbacks.length ?? 0;
    }

    private findEventCallbacksPair(eventType: string): EventCallbacksPair | undefined {
        return this.eventCallbacksPairs.find(pair => pair.eventType === eventType);
    }
}

const EVENT_BUS_KEY = Symbol.for('peppered.eventBus');

type GlobalWithEventBus = typeof globalThis & {
    [EVENT_BUS_KEY]?: EventBus;
    PepperedDebug?: {
        eventBus?: EventBus;
    };
};

function exposeEventBusForDebugging(eventBus: EventBus): void {
    const globalScope = globalThis as GlobalWithEventBus;

    globalScope.PepperedDebug ??= {};
    globalScope.PepperedDebug.eventBus = eventBus;
}

export function getEventBus(): EventBus {
    const globalScope = globalThis as GlobalWithEventBus;

    globalScope[EVENT_BUS_KEY] ??= new EventBus();

    exposeEventBusForDebugging(globalScope[EVENT_BUS_KEY]);

    return globalScope[EVENT_BUS_KEY];
}

export function setEventBus(eventBus: EventBus): void {
    const globalScope = globalThis as GlobalWithEventBus;

    globalScope[EVENT_BUS_KEY] = eventBus;

    exposeEventBusForDebugging(eventBus);
}

export function resetEventBus(): void {
    const globalScope = globalThis as GlobalWithEventBus;

    delete globalScope[EVENT_BUS_KEY];

    if (globalScope.PepperedDebug) {
        delete globalScope.PepperedDebug.eventBus;
    }
}

export default getEventBus();
